'use client';

import {
	BN,
	BigNum,
	ContractType,
	MarketType,
	PRICE_PRECISION_EXP,
	PerpMarketConfig,
	ZERO,
} from '@drift-labs/sdk';
import {
	ENUM_UTILS,
	OpenPosition,
	MARKET_UTILS,
	COMMON_UI_UTILS,
	UIMarket,
} from '@drift/common';
import { useCallback, useState, useMemo } from 'react';
import { useSwitchMarket } from 'src/hooks/driftActions/market/useSwitchMarket';
import useIsMobile from 'src/hooks/useIsMobileScreenSize';
import useDriftStore from '../../stores/DriftStore/useDriftStore';
import { cj } from 'src/utils/classNames';
import React from 'react';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import ContentGuard from '../ContentGuard';
import { useTableFiltersState } from 'src/hooks/useTableFiltersState';
import { AccountOpenPosition } from 'src/@types/positions';
import { OpenPositionTableRow } from './OpenPositionsRow';
import useDriftAccountStore from '../../stores/useDriftAccountsStore';
import {
	useCurrentAccountPositions,
	usePositionsByUserKey,
} from 'src/hooks/query/accountTradingData/usePositions';
import { useLeftoverIsolatedByUserKey } from 'src/hooks/query/accountTradingData/useLeftoverIsolatedPositions';
import { PageRoute } from 'src/constants/constants';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import Checkbox from 'src/components/primitives/Checkbox/Checkbox';
import useDriftClient from 'src/hooks/useDriftClient';
import ExpandAllButton from 'src/components/Buttons/ExpandAllButton';
import useModal from 'src/components/Modals/hooks/useModal';
import { BalanceDisplayType } from 'src/@types/types';
import { MOBILE_TABLE_CARD_LIST_CLASSNAME } from 'src/components/Tables/cards/consts';
import useExpandableItemList from 'src/hooks/lists/expandable/useExpandableItemList';
import { DataTable } from 'src/components/primitives/Table/DataTable';
import { getOpenPositionsColumns } from './openPositionsColumns';
import { useDrawersStore } from 'src/stores/useDrawersStore';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';
import IsolatedBalanceOnlyMobileCard from './IsolatedBalanceOnlyMobileCard';

interface Props {
	small?: boolean;
	overviewPage?: boolean;
}

const EMPTY_MESSAGE = 'No open positions found';

const OpenPositionsTable = (props: Props) => {
	const { show } = useModal();
	const [sizeDisplayType, setSizeDisplayType] =
		useState<BalanceDisplayType>('base');
	const {
		showOnlyCurrentMarket,
		showPerpMarkets,
		showPredictionMarkets,
		setShowOnlyCurrentMarket,
	} = useTableFiltersState(props);

	const selectedMarketIndex = useDriftStore(
		(s) => s.selectedMarket?.current?.market?.marketIndex
	);

	const driftClient = useDriftClient();

	const currentAccountClient = useDriftAccountStore(
		(s) => s.accounts[s.currentUserKey ?? '']?.client
	);
	const userKey = useDriftAccountStore((s) => s.currentUserKey);
	const currentPositionsLoaded = usePositionsByUserKey(userKey).data != null;
	const openPerpPositions = useCurrentAccountPositions();
	const currentAccountName = useDriftAccountStore(
		(s) => s.accounts[s.currentUserKey ?? '']?.name
	);
	const currentAccountUserKey = useDriftAccountStore(
		(s) => s.accounts[s.currentUserKey ?? '']?.userKey
	);
	const { data: leftoverIsolatedMarginPositions } =
		useLeftoverIsolatedByUserKey(currentAccountUserKey);

	const perpMarketInfoLookup =
		useMarketsInfoStore()?.allMarketsInfo?.perpLookup;

	// Move useMemo to top level to fix Rules of Hooks violation
	const ua = currentAccountClient?.getUserAccount();
	const rawPerpPosLookup = useMemo(() => {
		if (!ua) return new Map();
		const map = new Map();
		for (const position of ua.perpPositions) {
			if (!map.has(position.marketIndex)) {
				map.set(position.marketIndex, position);
			}
		}
		return map;
	}, [ua]);

	const displayPositions = useMemo((): AccountOpenPosition[] => {
		if (!currentPositionsLoaded) return [];
		if (!currentAccountClient) return [];
		if (!driftClient) return [];
		if (!perpMarketInfoLookup) return [];

		const isHighLeverageMode =
			currentAccountClient.isHighLeverageMode('Initial');
		const normalPositions: AccountOpenPosition[] =
			openPerpPositions
				?.filter((pos) => !pos.baseSize.eq(ZERO))
				.map((pos) => {
					const { maxLeverage, highLeverageMaxLeverage, hasHighLeverage } =
						MARKET_UTILS.getMaxLeverageForMarket(
							MarketType.PERP,
							pos.marketIndex,
							driftClient
						);
					const fallbackLev =
						isHighLeverageMode && hasHighLeverage
							? highLeverageMaxLeverage
							: maxLeverage;
					const sdkPerp = rawPerpPosLookup.get(pos.marketIndex);
					const positionMaxMarginRatio =
						sdkPerp?.maxMarginRatio ??
						(COMMON_UI_UTILS.convertLeverageToMarginRatio(
							fallbackLev
						) as number);

					const isIsolated = sdkPerp
						? currentAccountClient.isPerpPositionIsolated(sdkPerp)
						: false;

					return {
						...pos,
						accountName: currentAccountName ?? '',
						accountKey: currentAccountUserKey ?? '',
						isDustPosition: pos.baseSize
							.abs()
							.lt(
								perpMarketInfoLookup[
									pos.marketIndex
								].account.amm.orderStepSize.divn(10)
							),
						marketAccount: perpMarketInfoLookup[pos.marketIndex].account,
						positionMaxMarginRatio,
						isIsolated,
					};
				}) ?? [];

		const leftoverPositions: AccountOpenPosition[] = (
			leftoverIsolatedMarginPositions ?? []
		)
			.filter((pos) => perpMarketInfoLookup[pos.marketIndex])
			.map((pos) => {
				const marketAccount = perpMarketInfoLookup[pos.marketIndex].account;
				const marketSymbol = UIMarket.createPerpMarket(pos.marketIndex).symbol;
				const zeroBN = new BN(0);

				return {
					marketIndex: pos.marketIndex,
					marketSymbol,
					direction: 'long' as const,
					notional: zeroBN,
					baseSize: zeroBN,
					entryPrice: zeroBN,
					exitPrice: zeroBN,
					liqPrice: zeroBN,
					pnlVsOracle: zeroBN,
					pnlVsMark: zeroBN,
					quoteAssetNotionalAmount: zeroBN,
					quoteEntryAmount: zeroBN,
					quoteBreakEvenAmount: zeroBN,
					unrealizedFundingPnl: zeroBN,
					feesAndFundingPnl: zeroBN,
					lastCumulativeFundingRate: zeroBN,
					openOrders: 0,
					unsettledPnl: zeroBN,
					unsettledFundingPnl: zeroBN,
					totalUnrealizedPnl: zeroBN,
					costBasis: zeroBN,
					realizedPnl: zeroBN,
					pnlIsClaimable: false,
					maxMarginRatio: 0,
					accountName: currentAccountName ?? '',
					accountKey: currentAccountUserKey ?? '',
					marketAccount,
					isIsolated: true,
					isIsolatedBalanceOnly: true,
				};
			});

		return [...normalPositions, ...leftoverPositions];
	}, [
		currentPositionsLoaded,
		currentAccountClient,
		driftClient,
		perpMarketInfoLookup,
		openPerpPositions,
		rawPerpPosLookup,
		currentAccountName,
		currentAccountUserKey,
		leftoverIsolatedMarginPositions,
	]);

	const filteredPositions = useMemo(() => {
		const anyFiltersApplied =
			showPerpMarkets || showPredictionMarkets || showOnlyCurrentMarket;

		if (!anyFiltersApplied) return displayPositions;

		if (showOnlyCurrentMarket) {
			return displayPositions.filter(
				(pos) => pos.marketIndex === selectedMarketIndex
			);
		}

		return displayPositions.filter((pos) => {
			if (
				showPredictionMarkets &&
				ENUM_UTILS.match(
					pos.marketAccount.contractType,
					ContractType.PREDICTION
				)
			) {
				return true;
			}
			if (
				showPerpMarkets &&
				ENUM_UTILS.match(pos.marketAccount.contractType, ContractType.PERPETUAL)
			) {
				return true;
			}

			return false;
		});
	}, [
		displayPositions,
		showPerpMarkets,
		showPredictionMarkets,
		showOnlyCurrentMarket,
		selectedMarketIndex,
	]);

	const { switchMarket } = useSwitchMarket();
	const setDrawers = useDrawersStore((s) => s.set);

	const handleCloseAllPositions = useCallback(() => {
		show({
			type: 'close_all_positions',
			props: {
				perpMarketIndexesToClose: filteredPositions.map(
					(pos) => pos.marketIndex
				),
			},
		});
	}, [show, filteredPositions]);

	const showPnlModal = useCallback(
		(position: OpenPosition, pnl: number, isIsolated?: boolean) => {
			show({
				type: 'pnl_card',
				props: {
					position,
					actualExitPrice: BigNum.from(
						position.exitPrice,
						PRICE_PRECISION_EXP
					).toNum(),
					pnl,
					triggeredFrom: 'open_positions_table',
					isIsolated,
				},
			});
		},
		[show]
	);

	const isOnTradePage = useDriftStore(
		(s) => s.currentPageRoute === PageRoute.trade
	);

	const handleSelection = useCallback(
		(market: PerpMarketConfig) => {
			switchMarket({
				marketIndex: market.marketIndex,
				marketType: MarketType.PERP,
				goToTradePage: !isOnTradePage,
			});
		},
		[isOnTradePage, switchMarket]
	);

	const handleRowClick = useCallback(
		(position: AccountOpenPosition) => {
			setDrawers((s) => {
				s.position.show = true;
				s.position.marketIndex = position.marketIndex;
			});
		},
		[setDrawers]
	);

	const isMobile = useIsMobile();

	const {
		areAllItemsExpanded,
		handleItemExpandedChange,
		handleExpandAllItems,
		handleCollapseAllItems,
	} = useExpandableItemList({
		tableId: 'open-positions',
		numItems: filteredPositions.length,
	});

	const columns = useMemo(() => getOpenPositionsColumns(), []);

	const mobileTopControls = (
		<div className="flex w-full items-center justify-between p-3">
			<Checkbox.Default
				id="hide-other-assets"
				checked={!!showOnlyCurrentMarket}
				onCheckedChange={() => setShowOnlyCurrentMarket(!showOnlyCurrentMarket)}
				label="Hide others"
			/>
			<div className="flex items-center gap-x-2xl">
				<TextButton
					size="sm"
					variant="tertiary"
					onClick={handleCloseAllPositions}
				>
					Close All Positions
				</TextButton>

				<ExpandAllButton
					areAllItemsExpanded={areAllItemsExpanded}
					totalCount={filteredPositions.length}
					onExpandAll={handleExpandAllItems}
					onCollapseAll={handleCollapseAllItems}
				/>
			</div>
		</div>
	);

	if (isMobile) {
		return (
			<div
				className={cj(props.overviewPage && 'mb-4')}
				id="open_positions_table"
			>
				<ContentGuard
					requireAccount
					requireWallet
					isLoading={!currentPositionsLoaded}
				>
					<div className="bg-main-bg">{mobileTopControls}</div>
					<div
						className={cj(
							'w-full sm:max-h-full',
							MOBILE_TABLE_CARD_LIST_CLASSNAME
						)}
					>
						{filteredPositions.length > 0 ? (
							filteredPositions.map((position) => {
								const key = `${position.accountKey}-${position.direction}-${position.marketSymbol}-${position.baseSize}-${position.entryPrice}`;

								if (position.isIsolatedBalanceOnly) {
									return (
										<IsolatedBalanceOnlyMobileCard
											key={key}
											position={position}
											handleSelection={handleSelection}
										/>
									);
								}

								return (
									<OpenPositionTableRow
										key={key}
										position={position}
										isMobile={isMobile}
										tableGrid=""
										handleSelection={handleSelection}
										handleShowPnlModal={showPnlModal}
										areAllItemsExpanded={areAllItemsExpanded}
										handleItemExpandedChange={handleItemExpandedChange}
										sizeDisplayType={sizeDisplayType}
										onToggleSizeDisplayType={() =>
											setSizeDisplayType((prev) =>
												prev === 'base' ? 'notional' : 'base'
											)
										}
									/>
								);
							})
						) : (
							<MobileTableCard.EmptyPlaceholder>
								{EMPTY_MESSAGE}
							</MobileTableCard.EmptyPlaceholder>
						)}
					</div>
				</ContentGuard>
			</div>
		);
	}

	return (
		<ContentGuard
			requireAccount
			requireWallet
			isLoading={!currentPositionsLoaded}
		>
			<DataTable
				columns={columns}
				data={filteredPositions}
				onRowClick={handleRowClick}
				emptyMessage={EMPTY_MESSAGE}
				columnPinning={{ right: ['actions'] }}
				meta={{ onCloseAllPositions: handleCloseAllPositions }}
				dataTestId="open-positions-table"
			/>
		</ContentGuard>
	);
};

export default React.memo(OpenPositionsTable);
